import { ChevronDown, ChevronUp, Plus, Trash2 } from 'lucide-react'
import { AudioEditor } from '../../../../components/AudioEditor'
import { ImageDropZone, MultiImageDropZone } from '../../../../components/ImageDropZone'
import { SourceAudioOverview } from '../../../../components/SourceAudioOverview'

const formatTime = (seconds) => {
  if (seconds === undefined || seconds === null || Number.isNaN(Number(seconds))) return '--'
  const total = Math.max(0, Number(seconds))
  const minutes = Math.floor(total / 60)
  const secs = Math.floor(total % 60)
  const deciseconds = Math.floor((total - Math.floor(total)) * 10)
  return `${minutes}:${secs.toString().padStart(2, '0')}.${deciseconds}`
}

export function StoryReviewStep(props) {
  const { tx, story, setStep, handleSegmentTimeChange, addSegment,
    selectedSegmentIndex, setSelectedSegmentIndex, activeSeg, moveSegment,
    setShowDeleteSegmentModal, updateSegment, nudgeSegmentStart,
    nudgeSegmentEnd, updateSegmentWindow } = props
  return (
    <>
              {/* Step 3 */}
              {step === 3 && (
                <div className="space-y-4">
                  {story.source_audio && (
                    <SourceAudioOverview sourceAudio={story.source_audio} segments={story.segments} onReSplit={() => setStep(2)} onSegmentTimeChange={handleSegmentTimeChange} />
                  )}

	                  <div className="flex items-center justify-between">
	                    <div className="text-slate-800 text-sm font-extrabold">{tx({ zh: '段落（选择 → 修改 → 保存）', en: 'Segments (select → edit → save)' })}</div>
	                    <div className="flex items-center gap-2">
	                      <button onClick={() => setStep(2)} className="px-3 py-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold">
	                        {tx({ zh: '返回：切分', en: 'Back: Split' })}
	                      </button>
	                      <button onClick={addSegment} className="flex items-center gap-1 px-3 py-2 rounded-2xl bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-extrabold transition-colors border-2 border-emerald-600">
	                        <Plus size={14} />
	                        {tx({ zh: '新增段落', en: 'Add segment' })}
	                      </button>
	                    </div>
	                  </div>

                  <div className="grid md:grid-cols-[260px,1fr] gap-4">
                    {/* 列表 */}
                    <div className="border-2 border-slate-100 rounded-3xl bg-white p-2 max-h-[520px] overflow-y-auto">
                      {(story.segments || []).map((seg, idx) => {
                        const active = idx === selectedSegmentIndex
                        return (
                          <button
                            key={idx}
                            onClick={() => setSelectedSegmentIndex(idx)}
                            className={`w-full text-left px-3 py-2 rounded-2xl border-2 transition-colors ${
                              active ? 'bg-emerald-50 border-emerald-200' : 'bg-white border-slate-100 hover:bg-slate-50'
                            }`}
                          >
                            <div className="flex items-center justify-between">
                            <div className="text-slate-800 text-sm font-extrabold">
                              {tx({ zh: '段落 {n}', en: 'Segment {n}' }, { n: idx + 1 })}
                            </div>
                              <div className="text-[11px] text-slate-500 font-mono font-bold">
                                {formatTime(seg.start_time)} → {formatTime(seg.end_time)}
                              </div>
                            </div>
                            <div className="text-xs text-slate-500 truncate mt-1 font-medium">
                              {(seg.text || '').trim() ? (seg.text || '').trim() : tx({ zh: '（无文本）', en: '(No text)' })}
                            </div>
                          </button>
                        )
                      })}
                      {(story.segments || []).length === 0 && (
                        <div className="text-xs text-slate-500 px-3 py-2 font-medium">
                          {tx({ zh: '还没有段落：先去“切分”生成，或点右上角新增。', en: 'No segments yet: generate via Split, or add one with the top-right button.' })}
                        </div>
                      )}
                    </div>

                    {/* 编辑 */}
                    <div className="border-2 border-slate-100 rounded-3xl bg-white p-4 space-y-4">
                      {activeSeg ? (
                        <>
                          <div className="flex items-center justify-between">
                            <div className="text-slate-900 font-extrabold">
                              {tx({ zh: '段落 {n}', en: 'Segment {n}' }, { n: selectedSegmentIndex + 1 })}
                              <span className="ml-2 text-[11px] text-slate-500 font-mono font-bold">
                                {formatTime(activeSeg.start_time)} → {formatTime(activeSeg.end_time)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => moveSegment(selectedSegmentIndex, -1)}
                                disabled={selectedSegmentIndex === 0}
                                className="p-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors border-2 border-slate-100 disabled:opacity-30"
                                title={tx({ zh: '上移', en: 'Move up' })}
                              >
                                <ChevronUp size={14} />
                              </button>
                              <button
                                onClick={() => moveSegment(selectedSegmentIndex, 1)}
                                disabled={selectedSegmentIndex === (story.segments?.length || 0) - 1}
                                className="p-2 rounded-2xl bg-white hover:bg-slate-50 text-slate-500 hover:text-slate-900 transition-colors border-2 border-slate-100 disabled:opacity-30"
                                title={tx({ zh: '下移', en: 'Move down' })}
                              >
                                <ChevronDown size={14} />
                              </button>
                              <button
                                onClick={() => setShowDeleteSegmentModal(selectedSegmentIndex)}
                                className="p-2 rounded-2xl bg-rose-50 hover:bg-rose-100 text-rose-700 transition-colors border-2 border-rose-200"
                                title={tx({ zh: '删除段落', en: 'Delete segment' })}
                              >
                                <Trash2 size={14} />
                              </button>
                            </div>
                          </div>

                          <div className="grid md:grid-cols-[160px,1fr] gap-4">
                            <div>
                              <ImageDropZone
                                value={activeSeg.image}
                                onChange={(url) => updateSegment(selectedSegmentIndex, { image: url })}
                                label={tx({ zh: '封面', en: 'Cover' })}
                              />
                            </div>
                            <div className="space-y-2">
                              {/* 起止时间快速微调（更适合新手） */}
                              {activeSeg.start_time !== undefined && activeSeg.end_time !== undefined && (
                                <div className="bg-slate-50 border-2 border-slate-100 rounded-3xl p-3">
                                  <div className="flex items-center justify-between">
                                    <div className="text-xs text-slate-700 font-extrabold">
                                      {tx({ zh: '时间窗口（自动联动相邻段）', en: 'Time window (auto links adjacent segments)' })}
                                    </div>
                                    <div className="text-[11px] text-slate-500 font-mono font-bold">
                                      {formatTime(activeSeg.start_time)} → {formatTime(activeSeg.end_time)}
                                    </div>
                                  </div>
                                  <div className="grid grid-cols-2 gap-3 mt-2">
                                    <div>
                                      <div className="text-[11px] text-slate-500 mb-1 font-bold">{tx({ zh: '开始(s)', en: 'Start (s)' })}</div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => nudgeSegmentStart(selectedSegmentIndex, -1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '开始 -1s', en: 'Start -1s' })}
                                        >
                                          -1
                                        </button>
                                        <button
                                          onClick={() => nudgeSegmentStart(selectedSegmentIndex, -0.1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '开始 -0.1s', en: 'Start -0.1s' })}
                                        >
                                          -0.1
                                        </button>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={Number(activeSeg.start_time).toFixed(1)}
                                          onChange={(e) => updateSegmentWindow(selectedSegmentIndex, { start_time: Number(e.target.value) })}
                                        className="flex-1 bg-white border-2 border-slate-100 rounded-2xl px-2 py-1.5 text-slate-800 text-xs font-mono focus:border-emerald-400 focus:outline-none"
                                      />
                                        <button
                                          onClick={() => nudgeSegmentStart(selectedSegmentIndex, 0.1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '开始 +0.1s', en: 'Start +0.1s' })}
                                        >
                                          +0.1
                                        </button>
                                        <button
                                          onClick={() => nudgeSegmentStart(selectedSegmentIndex, 1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '开始 +1s', en: 'Start +1s' })}
                                        >
                                          +1
                                        </button>
                                      </div>
                                    </div>
                                    <div>
                                      <div className="text-[11px] text-slate-500 mb-1 font-bold">{tx({ zh: '结束(s)', en: 'End (s)' })}</div>
                                      <div className="flex items-center gap-1">
                                        <button
                                          onClick={() => nudgeSegmentEnd(selectedSegmentIndex, -1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '结束 -1s', en: 'End -1s' })}
                                        >
                                          -1
                                        </button>
                                        <button
                                          onClick={() => nudgeSegmentEnd(selectedSegmentIndex, -0.1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '结束 -0.1s', en: 'End -0.1s' })}
                                        >
                                          -0.1
                                        </button>
                                        <input
                                          type="number"
                                          step="0.1"
                                          value={Number(activeSeg.end_time).toFixed(1)}
                                          onChange={(e) => updateSegmentWindow(selectedSegmentIndex, { end_time: Number(e.target.value) })}
                                        className="flex-1 bg-white border-2 border-slate-100 rounded-2xl px-2 py-1.5 text-slate-800 text-xs font-mono focus:border-emerald-400 focus:outline-none"
                                      />
                                        <button
                                          onClick={() => nudgeSegmentEnd(selectedSegmentIndex, 0.1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '结束 +0.1s', en: 'End +0.1s' })}
                                        >
                                          +0.1
                                        </button>
                                        <button
                                          onClick={() => nudgeSegmentEnd(selectedSegmentIndex, 1)}
                                          className="px-2 py-1.5 rounded-2xl bg-white hover:bg-slate-50 text-slate-600 hover:text-slate-900 transition-colors border-2 border-slate-100 text-xs font-bold"
                                          title={tx({ zh: '结束 +1s', en: 'End +1s' })}
                                        >
                                          +1
                                        </button>
                                      </div>
                                    </div>
                                  </div>
                                  <div className="text-[11px] text-slate-500 mt-2 font-medium">
                                    {tx({
                                      zh: '建议：先用 ±1s 粗调，再用 ±0.1s 精调；时长会自动更新。',
                                      en: 'Tip: use ±1s for coarse adjustment, then ±0.1s for fine adjustment; duration updates automatically.',
                                    })}
                                  </div>
                                </div>
                              )}
                              <textarea
                                value={activeSeg.text}
                                onChange={(e) => updateSegment(selectedSegmentIndex, { text: e.target.value })}
                                rows={4}
                                placeholder={tx({ zh: '输入段落文本...', en: 'Enter segment text…' })}
                                className="w-full bg-white border-2 border-slate-100 rounded-2xl px-3 py-2 text-slate-800 text-sm resize-none focus:border-emerald-400 focus:ring-2 focus:ring-emerald-100 outline-none"
                              />
                              <div className="grid grid-cols-2 gap-2">
                                <div>
                                  <div className="text-[11px] text-slate-500 mb-1 font-bold">{tx({ zh: '时长(ms)', en: 'Duration (ms)' })}</div>
                                  <input
                                    type="number"
                                    value={activeSeg.duration_ms}
                                    onChange={(e) => updateSegment(selectedSegmentIndex, { duration_ms: e.target.value })}
                                    className="w-full bg-white border-2 border-slate-100 rounded-2xl px-2 py-1.5 text-slate-800 text-xs focus:border-emerald-400 focus:outline-none"
                                  />
                                </div>
                                <div>
                                  <div className="text-[11px] text-slate-500 mb-1 font-bold">{tx({ zh: '帧间隔(ms)', en: 'Frame interval (ms)' })}</div>
                                  <input
                                    type="number"
                                    value={activeSeg.frame_duration_ms}
                                    onChange={(e) => updateSegment(selectedSegmentIndex, { frame_duration_ms: e.target.value })}
                                    className="w-full bg-white border-2 border-slate-100 rounded-2xl px-2 py-1.5 text-slate-800 text-xs focus:border-emerald-400 focus:outline-none"
                                  />
                                </div>
                              </div>
                            </div>
                          </div>

                          <MultiImageDropZone
                            images={activeSeg.extraImages ? activeSeg.extraImages.split('\n').filter(Boolean) : []}
                            onChange={(urls) => updateSegment(selectedSegmentIndex, { extraImages: urls.join('\n') })}
                            label={tx({ zh: '动画帧图片（可选）', en: 'Animation frames (optional)' })}
                          />

                          <AudioEditor
                            value={activeSeg.audio_url}
                            onChange={(url) => updateSegment(selectedSegmentIndex, { audio_url: url })}
                            label={tx({ zh: '该段落覆盖音频（可选，不填则默认用源音频段）', en: 'Override audio for this segment (optional; defaults to source audio)' })}
                          />
                        </>
                      ) : (
                        <div className="text-xs text-slate-500 font-medium">{tx({ zh: '没有可编辑的段落。', en: 'No segment selected.' })}</div>
                      )}
                    </div>
                  </div>
                </div>
              )}
    </>
  )
}
